/*
  スマホ (767px 以下) のトップと料金のページで、PC では横に並ぶ札を縦に積まず、横の列のまま指と矢印で送る。
  代表の指示 (2026-10-21) は「スマホの縦が長すぎる。安直に縦並びにしない」。
  作りは受講生の声の列 (VoicesShowcase、sec-phase1.ts の hx-vcards と ROW_ARROWS_CSS) と同じで、
  囲みに data-hx-rowbox、列に data-hx-row、列の上に矢印の帯 (components/MobileSlideBar.tsx) を置く。
  札は 1 枚を画面の 84% にし、次の札の頭を右に見せて「まだある」と分からせる。同じ列の札は同じ高さにそろえる。
  帯の左の細い線は、どこまで送ったかの目安。CSS の scroll-timeline だけで動き、効かない端末では線を隠す。
  768px から上 (PC とタブレット) は今までの並びのまま。帯も札の列も出さない。
  規則はトップ (.hp-home。広告用の写しと v1 も同じ) と料金のページ (.pp-page) に当てる。同じ部品を両方で使うので、見え方をそろえる
*/
const H = ".hp:is(.hp-home,.pp-page)";
/* 1 枚を画面の 84% にする列 */
const WIDE = `${H} .em-need,${H} .em-do,${H} .pp-pay-list,${H} .hx-i-crit-l,${H} .pp-pc-list`;
/* 縦長の顔の札は 84% にすると 1 枚で 440px を超え、2 列の格子より長くなる。2 枚弱と次の頭が見える幅にする */
const FACES = `${H} .hx-i-grid`;
/* 顔の札の幅と、写真 (縦 4 横 3) の高さ。経歴を開いたときの最小の高さに使う */
const FACE_W = "((100vw - 52px) / 1.8)";

export const MROW_CSS = `
.hp .hx-mrow-bar,.hp .pp-pc-list{display:none}
@keyframes hx-mrow-p{from{scale:.3 1}to{scale:1 1}}
@media (max-width:767px){
  /* スマホの縦の間合い。節の上下 (--sec) と、節の中の塊どうしの間 (--gap) を、PC の縮小ではなく電話の 1 画面に合う値にそろえる。
     自前の値を持つ節 (声、ジグザグ学習、エンジニアになるなら、受講料に込み) はその値のまま */
  html ${H}{--sec:48px;--gap:28px}
  /* 黒い帯の節 (BrandHome、楽して人生が変わる、30ヶ月) は自前の 56px を持つ。ほかの節と同じ上下にそろえる */
  ${H} .bp{padding-block:var(--sec)}

  ${H} .hx-mrow-bar{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:var(--gap)}
  ${H} .hx-mrow-prog{flex:1;max-width:96px;height:3px;border-radius:2px;background:var(--line,#D9D9D9);overflow:hidden}
  ${H} .hx-mrow-prog i{display:block;width:100%;height:100%;background:var(--ink);transform-origin:left center;scale:.3 1}
  @supports (animation-timeline:scroll()){
    ${H} [data-hx-rowbox]:has(>.hx-mrow-bar){timeline-scope:--hx-mrow}
    ${H} [data-hx-rowbox]:has(>.hx-mrow-bar) [data-hx-row]{scroll-timeline:--hx-mrow x}
    ${H} .hx-mrow-prog i{animation:hx-mrow-p linear both;animation-timeline:--hx-mrow}
  }
  @supports not (animation-timeline:scroll()){${H} .hx-mrow-prog{visibility:hidden}}

  /* 列の作り。左右は節の余白 (20px) の外まで出して、端の札が切れずに入ってくるようにする */
  ${WIDE},${FACES}{display:flex;gap:12px;align-items:stretch;margin:12px -20px 0;padding:0 20px 8px;overflow-x:auto;overflow-y:hidden;overscroll-behavior-x:contain;scroll-snap-type:x mandatory;scroll-padding-left:20px;scrollbar-width:none;list-style:none}
  :is(${WIDE},${FACES})::-webkit-scrollbar{display:none}
  :is(${WIDE})>*{flex:none;width:84vw;max-width:520px;min-width:0;margin-top:0;scroll-snap-align:start}
  ${FACES}>*{flex:none;width:calc${FACE_W};min-width:0;scroll-snap-align:start}
  /* お支払い方法の 1 枚目は、月々の額の表と長い回数の補足を <details> に畳んである (畳んだ高さで 2 枚目の約 1.7 倍)。
     開くと 3 倍近くになり、高さをそろえると 2 枚目と 3 枚目の枠の中が大きく空になるので、この列だけ札の高さはそろえない */
  ${H} .pp-pay-list{align-items:start}

  /* 講師の審査基準の札。縦の積みのときの「アイコン | 字」の組みはそのまま */
  ${H} .hx-i-crit-l{margin-top:12px}

  /* 講師の経歴を押して開いたとき。写真の枠の中に収まらない人がいる (360px で 20px ほど) ので、
     開いている間だけ枠を中身の高さまで伸ばす。閉じた形と、写真の縦 4 横 3 は今のまま。伸びても横の列の高さが変わるだけ */
  ${H} .hx-i-card[data-open] .hx-i-photo,html:not(.hx-i-js) ${H} .hx-i-card:focus .hx-i-photo{aspect-ratio:auto;min-height:calc(${FACE_W} * 4 / 3)}
  ${H} .hx-i-card[data-open] .hx-i-photo>img,html:not(.hx-i-js) ${H} .hx-i-card:focus .hx-i-photo>img{position:absolute;inset:0}
  ${H} .hx-i-card[data-open] .hx-i-over,html:not(.hx-i-js) ${H} .hx-i-card:focus .hx-i-over{position:relative;min-height:calc(${FACE_W} * 4 / 3 - 2px)}

  /* 料金表。表 (行の高さを 3 プランでそろえる) の代わりに、プランごとの札 (PlanTable の pp-pc-list) を出す。
     1 枚しか見えないので、欄は札ごとに中身の高さにする */
  ${H} .pp-pt-box{display:none}
  /* 札の高さはそろえる (背がばらばらだと並べて比べにくい)。「おすすめ」の札は札の頭の行の中に置き、札の外へはみ出さない */
  ${H} .pp-pc-list{display:flex;align-items:stretch;margin-top:12px}
  ${H} .pp-pc{position:relative;display:flex;flex-direction:column;border:1px solid var(--w-rule,#D9D9D9);border-radius:6px;background:var(--cream)}
  ${H} .pp-pc.yes{border:3px solid var(--ink);background:#fff}
  ${H} .pp-pc-flag{flex:none;padding:3px 12px;background:var(--yellow);color:var(--ink);font-size:12px;font-weight:900;line-height:1.5;border-radius:999px}
  ${H} .pp-pc-h{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:6px;margin:0;padding:12px 10px;border-bottom:1px solid var(--w-rule,#D9D9D9);font-size:15px;font-weight:900;line-height:1.5;color:var(--ink)}
  ${H} .pp-pc-h .pp-pt-ic{margin:0}
  ${H} .pp-pc-rows{margin:0;padding:0 14px}
  ${H} .pp-pc-rows>div{padding:12px 0;border-top:1px solid var(--w-rule,#D9D9D9);text-align:center}
  ${H} .pp-pc-rows>div:first-child{border-top:0}
  ${H} .pp-pc-rows dt{display:flex;align-items:center;justify-content:center;gap:4px;margin-bottom:4px;font-size:12px;font-weight:900;line-height:1.4;color:var(--sub)}
  ${H} .pp-pc-ri{flex:none}
  ${H} .pp-pc-rows dd{margin:0;font-size:14px;font-weight:700;line-height:1.6;color:var(--ink);word-break:auto-phrase;text-wrap:pretty}
  ${H} .pp-pc.yes .pp-pc-rows dd{font-weight:900;font-size:15px}
  ${H} .pp-pc .pp-pt-pr{display:inline-block;font-size:clamp(26px,8vw,34px)}
  ${H} .pp-pc.yes .pp-pt-pr{background:linear-gradient(transparent 60%,var(--yellow) 60%)}

  /* 学習の中身の章の一覧。枠 (BrowserFrame) の中の段落の余白 (16px) の外まで出して、章を 1 枚ずつ送る */
  ${H} .hx-pd-chbox .hx-mrow-bar{margin-top:16px}
  ${H} .hx-pd-ch{display:flex;gap:12px;align-items:stretch;margin:12px -16px 0;padding:0 16px 8px;border-top:0;overflow-x:auto;overflow-y:hidden;overscroll-behavior-x:contain;scroll-snap-type:x mandatory;scroll-padding-left:16px;scrollbar-width:none}
  ${H} .hx-pd-ch::-webkit-scrollbar{display:none}
  ${H} .hx-pd-ch>.hx-pd-row{flex:none;width:78vw;max-width:440px;min-width:0;align-content:start;padding:12px 14px;border:1px solid var(--line);border-radius:6px;background:#fff;scroll-snap-align:start}
  ${H} .hx-pd-ch>.hx-pd-row:last-child{border-bottom:1px solid var(--line)}
  /* 札の幅は表のときより狭いので、手順の名前は語のまとまりで折る (「はじめ / 方」のような折れを避ける) */
  ${H} .hx-pd-ch li{word-break:auto-phrase;text-wrap:pretty}
}
`;
